import type {
  Availability,
  EventStatus,
  RenderMode,
  SourceHealth,
  VerificationLevel,
} from '@/api/types';
import { formatAge, formatDateTime, formatObservedDay } from '@/lib/format';
import { shortSeriesNote } from '@/lib/series';

function plural(count: number, one: string, few: string, many: string): string {
  const mod100 = count % 100;
  const mod10 = count % 10;
  if (mod100 >= 11 && mod100 <= 14) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

/**
 * §5.4: уровень доказательности — видимый элемент, а не сноска.
 * `fact` в перечислении контракта отсутствует, и это принципиально.
 */
export function VerificationBadge({
  level,
  sources,
  compact = false,
}: {
  level: VerificationLevel;
  sources: number;
  /**
   * T50: плотная строка каталога. Полная подпись «гипотеза · 1 источник»
   * занимала там три строки и задавала высоту 82 px всей строке таблицы —
   * ровно то «строчки съезжают», о котором сказал владелец. В сжатом виде
   * остаётся уровень и число источников, а полная формулировка уходит в
   * подсказку: смысл не теряется, ритм списка сохраняется.
   */
  compact?: boolean;
}) {
  const word = level === 'hypothesis' ? 'гипотеза' : 'подтверждено';
  // 🔴 «гипотеза · 0 источников» читалось как «данных нет вообще», хотя число
  // означает другое: сколько источников НЕЗАВИСИМО подтвердили движение цены.
  // У своего пакета их не бывает по построению — он собран нами из двух
  // половин, и подтверждать его некому (найдено живым показом 26.07).
  const detail =
    sources > 0
      ? `${sources} ${plural(sources, 'источник', 'источника', 'источников')}`
      : 'подтверждений нет';
  const full = `${word} · ${detail}`;
  if (compact) {
    const short = level === 'hypothesis' ? 'гип.' : 'подтв.';
    return (
      <span
        title={full}
        aria-label={full}
        className="whitespace-nowrap text-[11px] text-ink-dim"
      >
        {sources > 0 ? `${short} ${sources}` : short}
      </span>
    );
  }
  return <span className="text-[11px] text-ink-dim">{full}</span>;
}

export function FreshnessTag({
  freshnessSec,
  isCached,
  cacheExpired,
}: {
  freshnessSec: number;
  isCached: boolean;
  cacheExpired: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-ink-dim">
      {isCached ? <span aria-hidden="true">⛁</span> : null}
      {isCached ? <span>{cacheExpired ? 'кэш устарел' : 'кэш'}</span> : null}
      <span>{formatAge(freshnessSec)}</span>
    </span>
  );
}

/**
 * T39, Д-Т39-2: за этой границей цена перестаёт быть сегодняшней.
 *
 * 36 часов — не новое число: тот же возраст, за которым замер считается
 * историей при расчёте фазы цены и не может быть текущей ценой варианта
 * (T38). На проде 2026-07-27 за него выходят 22 % цен витрины, тогда как
 * порог в 6 часов пометил бы 63 % строк и превратился бы в шум.
 */
export const PRICE_AGE_THRESHOLD_SEC = 36 * 3600;

/**
 * Когда цену подтверждали последний раз — говорится ТОЛЬКО если она не
 * сегодняшняя (Д-Т39-1).
 *
 * Формулировка — дата, а не «устарела» (Д-Т39-3): изменилась ли цена, система
 * не знает; она знает лишь, когда видела её. Цвет не задействован (Д-Т39-4):
 * палитра зарезервирована под вспышку срыва и фото отелей.
 */
export function StalePriceNote({
  freshnessSec,
  observedAt,
  timezone,
}: {
  freshnessSec: number;
  observedAt: string | null;
  timezone: string;
}) {
  if (!Number.isFinite(freshnessSec) || freshnessSec <= PRICE_AGE_THRESHOLD_SEC) {
    return null;
  }

  const when = observedAt === null ? null : formatObservedDay(observedAt, timezone);

  return (
    <span
      data-testid="stale-price"
      data-stale="true"
      className="inline-flex items-baseline gap-1 text-[11px] text-ink-dim"
    >
      <span aria-hidden="true">⧗</span>
      <span>{when === null ? formatAge(freshnessSec) : `цена от ${when}`}</span>
    </span>
  );
}

const AVAILABILITY_TEXT: Record<Availability, string | null> = {
  available: null,
  last_seats: 'мест мало',
  sold_out: 'мест нет',
  lt_24h: 'меньше суток',
  not_found: 'вариант исчез',
};

export function AvailabilityTag({ availability }: { availability: Availability }) {
  const text = AVAILABILITY_TEXT[availability];
  if (!text) return null;
  return <span className="text-[11px] text-ink-dim">{text}</span>;
}

const HEALTH_TEXT: Record<SourceHealth, { icon: string; text: string; broken: boolean }> = {
  healthy: { icon: '✓', text: 'живой', broken: false },
  degraded: { icon: '⚠', text: 'с перебоями', broken: false },
  broken: { icon: '✕', text: 'нет ответа', broken: true },
};

/** §4.2: состояние читается иконкой И подписью. Цвет — только подкрепление. */
export function SourceStatus({ health, error }: { health: SourceHealth; error?: string | null }) {
  const { icon, text, broken } = HEALTH_TEXT[health];
  const filteredResponse = error === 'filtered_empty';
  const label = broken && filteredResponse ? 'ответ отклонён' : text;
  const explanation = filteredResponse ? 'полученные предложения не соответствуют условиям поиска' : error;
  return (
    <span
      className={
        broken
          ? 'inline-flex flex-wrap items-center gap-1 text-broken'
          : 'inline-flex flex-wrap items-center gap-1 text-ink-dim'
      }
    >
      <span aria-hidden="true">{icon}</span>
      <span className="text-[11px]">{label}</span>
      {explanation ? <span className="text-[11px] text-ink-dim">· {explanation}</span> : null}
    </span>
  );
}

const EVENT_STATUS_TEXT: Record<EventStatus, string> = {
  new: 'новое',
  seen: 'просмотрено',
  verified: 'проверено',
  false_positive: 'оказалось ложным',
};

export function EventStatusPill({ status }: { status: EventStatus }) {
  return (
    <span className="rounded border border-rule px-2 py-0.5 text-[11px] text-ink-dim">
      {EVENT_STATUS_TEXT[status]}
    </span>
  );
}

export function ShortSeriesNote({ mode }: { mode: RenderMode }) {
  const note = shortSeriesNote(mode);
  if (!note) return null;
  return <span className="text-[11px] text-ink-dim">{note}</span>;
}

/**
 * Д-В9 и Д-Ф12: вердикт посчитан тиком относительно ватерлинии наблюдений.
 * Между тиками замеры приходят, а вердикт — нет. Показать его молча как
 * актуальный значит соврать; пересчитать своей формулой — тем более
 * (Global Constraint 15: детектору нужна последовательность наблюдений,
 * из median/MAD/p25 вердикт не выводится).
 *
 * Вспышки здесь нет намеренно: устаревший срыв не подтверждён свежими данными.
 */
export function StaleVerdictNote({
  isStale,
  computedAt,
  timezone,
}: {
  isStale: boolean;
  computedAt: string | null;
  timezone: string;
}) {
  if (!isStale) return null;

  const when = computedAt === null ? null : formatDateTime(computedAt, timezone);

  return (
    <span
      data-testid="stale-verdict"
      data-stale="true"
      className="inline-flex items-baseline gap-1 text-[11px] text-ink-dim"
    >
      <span aria-hidden="true">⧗</span>
      <span>
        {when === null
          ? 'вердикт посчитан до последних замеров'
          : `вердикт посчитан до последних замеров · ${when}`}
      </span>
    </span>
  );
}
